import { Tabs, TabsList, TabsTrigger } from "@/components/ui/baseUi/tabs"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { ProgramSasPendingPaymentView } from "@modules/programs/programSas/components/programSasPendingPaymentView"
import { ProgramSasPendingRequestView } from "@modules/programs/programSas/components/programSasPendingRequestView"
import { useProgramSasRequestTotal } from "@modules/programs/programSas/hooks/useProgramSasRequestTotal"
import { ArrowLeft } from "lucide-react"
import { startTransition, useState } from "react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramSasLayoutProps = {
  programId: string
  hideProgramSasLayout: () => void
  className?: string
} & React.ComponentProps<"div">

export function ProgramSasLayout({ programId, hideProgramSasLayout, className, ...props }: ProgramSasLayoutProps) {
  const [programSasLayoutIndex, setProgramSasLayoutIndex] = useState("0")
  const { data: sasRequestTotal } = useProgramSasRequestTotal(programId)

  if (sasRequestTotal.allPending === 0) {
    hideProgramSasLayout()
  } else if (
    programSasLayoutIndex === "0" &&
    sasRequestTotal.pendingRequestTotal === 0 &&
    sasRequestTotal.pendingPaymentTotal > 0
  ) {
    setProgramSasLayoutIndex("1")
  } else if (
    programSasLayoutIndex === "1" &&
    sasRequestTotal.pendingPaymentTotal === 0 &&
    sasRequestTotal.pendingRequestTotal > 0
  ) {
    setProgramSasLayoutIndex("0")
  }

  function selectTab(index: string) {
    startTransition(() => {
      setProgramSasLayoutIndex(index)
    })
  }

  return (
    <div className={cn("flex grow flex-col gap-8", className)} {...props}>
      <div className="flex w-full items-center">
        <ArrowLeft strokeWidth={1.5} size={26} onClick={() => hideProgramSasLayout()} className="cursor-pointer" />
        <Text fontStyle="TS" className="flex grow justify-center text-center">
          {word("program.sas.list.header.title")}
        </Text>
      </div>
      <div className={cn("flex w-full flex-col gap-1", className)} {...props}>
        {sasRequestTotal.pendingRequestTotal > 0 && sasRequestTotal.pendingPaymentTotal == 0 ? (
          <Text fontStyle="BS" className="text-textDefault-caption">
            {word("program.sas.tab.pendingRequest", {
              count: sasRequestTotal.pendingRequestTotal,
            }).toLocaleUpperCase()}
          </Text>
        ) : sasRequestTotal.pendingPaymentTotal > 0 && sasRequestTotal.pendingRequestTotal == 0 ? (
          <Text fontStyle="BS" className="text-textDefault-caption">
            {word("program.sas.tab.pendingPayement", {
              count: sasRequestTotal.pendingPaymentTotal,
            }).toLocaleUpperCase()}
          </Text>
        ) : (
          <>
            <Text fontStyle="BS" className="text-textDefault-caption">
              {word("program.sas.tab.statusOfRequest").toLocaleUpperCase()}
            </Text>
            <Tabs defaultValue={programSasLayoutIndex} className="flex w-full flex-col">
              <TabsList variant="roundedButton">
                <TabsTrigger variant="roundedButton" onClick={() => selectTab("0")} value="0">
                  {word("program.sas.tab.pendingRequest", {
                    count: sasRequestTotal.pendingRequestTotal,
                  })}
                </TabsTrigger>
                <TabsTrigger variant="roundedButton" onClick={() => selectTab("1")} value="1">
                  {word("program.sas.tab.pendingPayement", {
                    count: sasRequestTotal.pendingPaymentTotal,
                  })}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </>
        )}
      </div>

      {programSasLayoutIndex === "0" && <ProgramSasPendingRequestView programId={programId} />}
      {programSasLayoutIndex === "1" && <ProgramSasPendingPaymentView programId={programId} />}
    </div>
  )
}
